import { useState } from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw } from 'lucide-react';
import { ConfirmDialog } from '@/components/core/common/feedback/ConfirmDialog';

export interface RestartButtonProps {
  /** "flashcards" / "quiz" - names the button and the question. */
  what: 'flashcards' | 'quiz';
  /** Whether there is progress to lose. Without any, restart just runs. */
  hasProgress: boolean;
  onRestart: () => void;
}

/**
 * ↻ for a practice run, which asks first. It sat in the header one stray tap
 * from wiping a half-done deck or quiz with no way back - on a phone, right
 * where a thumb reaches for the top of the card.
 */
export function RestartButton({
  what,
  hasProgress,
  onRestart,
}: RestartButtonProps) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      {/* `aria-label`, not `title` alone: an icon-only button whose only name
          is a tooltip is announced as just "button" by some screen readers. */}
      <button
        type='button'
        onClick={() => (hasProgress ? setAsking(true) : onRestart())}
        aria-label={`Restart ${what}`}
        title={`Restart ${what}`}
        className='tap-target text-muted-foreground transition-colors hover:text-foreground'
      >
        <RotateCcw className='h-4 w-4' aria-hidden='true' />
      </button>
      {/* Portalled: `ConfirmDialog` is `fixed`, and the practice tab body
          animates with a transform, which would trap it inside the tab. */}
      {createPortal(
        <ConfirmDialog
          isOpen={asking}
          onClose={() => setAsking(false)}
          onConfirm={() => {
            setAsking(false);
            onRestart();
          }}
          title='Start over?'
          description={
            what === 'quiz'
              ? 'Your answers in this run will be cleared.'
              : 'Your ratings in this deck will be cleared.'
          }
          confirmText='Start over'
          variant='warning'
        />,
        document.body
      )}
    </>
  );
}
